import type RouterService from '@ember/routing/router-service';
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { addMinutes } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { task } from 'ember-concurrency';
import MapSearchQuery from 'garaje/graphql/queries/MapSearchQuery';
import type ApolloService from 'garaje/services/apollo-extension';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';

const CURRENT_STATE_INTERVAL_MINUTES = 5;

// Define types for the search results
interface MapFeatureSearchResult {
  type: string;
  __typename: 'MapFeatureSearchResultItem';
  feature: {
    id: string;
    name: string;
    floor: {
      id: string;
      name: string;
      __typename: 'MapFloor';
    };
    __typename: 'MapPointOfInterestFeature';
  };
}

interface EmployeeSearchResult {
  type: string;
  __typename: 'EmployeeMapSearchResultItem';
  employee: {
    id: number;
    name: string;
    email: string;
    profilePictureUrl?: string;
  };
}

type MapSearchResultItem = MapFeatureSearchResult | EmployeeSearchResult;

// Resource types that should be categorized as "resources"
const RESOURCE_TYPES = ['parking', 'desk', 'room'];

// GraphQL query types
interface MapSearchQueryVariables {
  term: string;
  locationId: string;
  startTime: string;
  endTime: string;
  timeoutMilliseconds?: number;
  includeMapFeatures: boolean;
  includeEmployees: boolean;
  draftId?: string;
}

interface MapSearchQueryType {
  __typename: 'MapSearchResults';
  term: string;
  locationId: string;
  startTime: string;
  endTime: string;
  includeEmployees: boolean;
  includeMapFeatures: boolean;
  draftId?: string;
  items: MapSearchResultItem[];
}

export default class MapSearchService extends Service {
  @service declare apolloExtension: ApolloService;
  @service declare state: StateService;
  @service declare flashMessages: FlashMessagesService;
  @service declare router: RouterService;

  // Search results
  @tracked searchResults: MapSearchResultItem[] = [];
  @tracked isSearching = false;
  @tracked shouldClearInput = false;

  _toOfficeLocationTime(date: Date): Date {
    return this.state.getOfficeLocationTime(date);
  }

  _getStartEndTime(): { startTime: string; endTime: string } {
    const startTime = toZonedTime(new Date(), this.state.currentLocation.timezone);
    const endTime = addMinutes(startTime, CURRENT_STATE_INTERVAL_MINUTES);

    return {
      startTime: this._toOfficeLocationTime(startTime).toISOString(),
      endTime: this._toOfficeLocationTime(endTime).toISOString(),
    };
  }

  searchMapFeaturesAndEmployees = task({ drop: true }, async (searchTerm: string, options?: { draftId?: string }) => {
    if (this.searchResults.length > 0) {
      this.searchResults = [];
    }

    if (!searchTerm.trim()) {
      this.searchResults = [];
      return;
    }

    this.isSearching = true;

    try {
      const locationId = this.state.currentLocation?.id;
      if (!locationId) {
        throw new Error('No current location available');
      }

      const { startTime, endTime } = this._getStartEndTime();

      const variables: MapSearchQueryVariables = {
        term: searchTerm,
        locationId,
        startTime,
        endTime,
        timeoutMilliseconds: 5000, // 5 second timeout
        includeMapFeatures: true,
        includeEmployees: true,
        ...(options?.draftId && { draftId: options.draftId }),
      };

      const results = (await this.apolloExtension.query({
        query: MapSearchQuery,
        fetchPolicy: 'network-only',
        variables,
      })) as unknown as { mapSearch: MapSearchQueryType };

      if (results?.mapSearch?.items) {
        this.searchResults = results.mapSearch.items;
      } else {
        this.searchResults = [];
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Map search failed:', error);
      this.flashMessages.showFlash('error', 'Search failed. Please try again.');
      this.searchResults = [];
    } finally {
      this.isSearching = false;
    }
  });

  /**
   * Get resources (parking, desk, rooms) from search results
   * Preserves the original order of results
   */
  getResources(): MapFeatureSearchResult[] {
    return this.searchResults.filter(
      (item): item is MapFeatureSearchResult =>
        item.__typename === 'MapFeatureSearchResultItem' && this.isResourceType(item.feature.name),
    );
  }

  /**
   * Get points of interest (all other resource types) from search results
   * Preserves the original order of results
   */
  getPointOfInterests(): MapFeatureSearchResult[] {
    return this.searchResults.filter(
      (item): item is MapFeatureSearchResult =>
        item.__typename === 'MapFeatureSearchResultItem' && !this.isResourceType(item.feature.name),
    );
  }

  /**
   * Get employees from search results
   * Preserves the original order of results
   */
  getEmployees(): EmployeeSearchResult[] {
    return this.searchResults.filter(
      (item): item is EmployeeSearchResult => item.__typename === 'EmployeeMapSearchResultItem',
    );
  }

  /**
   * Check if a feature name indicates it's a resource type
   */
  private isResourceType(featureName: string | undefined): boolean {
    if (!featureName) {
      return false;
    }
    const lowerName = featureName.toLowerCase();
    return RESOURCE_TYPES.some((type) => lowerName.includes(type));
  }

  /**
   * Clear search results
   */
  clearSearchResults(): void {
    this.searchResults = [];
  }

  /**
   * Get all map features (both resources and points of interest)
   */
  getAllMapFeatures(): MapFeatureSearchResult[] {
    return this.searchResults.filter(
      (item): item is MapFeatureSearchResult => item.__typename === 'MapFeatureSearchResultItem',
    );
  }
}
